import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertRegular,
  ArrowClockwiseFilled,
  DismissRegular,
  HistoryRegular,
  TableRegular
} from '@fluentui/react-icons';
import {
  makeStyles,
  mergeClasses,
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Dropdown,
  Input,
  Option,
  MessageBar,
  MessageBarActions,
  MessageBarTitle,
  MessageBarBody,
  Tooltip
} from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import { useData } from '../providers/DataContext';
import GrafanaDashboardConfig from './GrafanaDashboardConfig';
import RecipesTargetTable from './RecipesTargetTable';
import RecipeHistoryTable from './RecipeHistoryTable';
import AlertsTable from './AlertsTable';
import { REASON_OPTIONS, getReasonLabel } from '../constants/reasons';

const RECIPE_HISTORY_LIMIT = 50;

// Position the listboxes with `position: fixed` instead of `absolute`. An
// absolutely positioned listbox at the bottom of the page briefly enlarges the
// scrollable area while Fluent measures it, which flickers a scrollbar and
// resizes the embedded report. A fixed one never affects page overflow.
const DROPDOWN_POSITIONING = { strategy: 'fixed' };

// The full row needs about 1540px; below that it wraps to two rows, and
// below the medium layout's own minimum it stacks into one column.
const MEDIUM_SCREEN = '@media (max-width: 1559px)';
const NARROW_SCREEN = '@media (max-width: 959px)';

const useStyles = makeStyles({
  root: {
    position: 'relative',
  },
  // Pinned to the top-left corner of the form area on wide screens, inline
  // with the other buttons once the form wraps.
  configButton: {
    position: 'absolute',
    top: '10px',
    left: '10px',
    [MEDIUM_SCREEN]: {
      position: 'static',
    },
  },
  // One row on wide screens, two rows on medium ones, a single column on
  // narrow ones. DOM order matches the visual order at every breakpoint.
  formRoot: {
    display: 'grid',
    gap: '20px',
    alignItems: 'end',
    justifyContent: 'center',
    padding: '40px 20px',
    gridTemplateColumns: 'auto 250px auto 250px minmax(180px, 480px) auto',
    gridTemplateAreas: '"buttons recipe numbers reason comment submit"',
    [MEDIUM_SCREEN]: {
      gridTemplateColumns: 'auto 250px minmax(180px, 1fr) auto',
      gridTemplateAreas: '"buttons recipe numbers numbers" ". reason comment submit"',
    },
    [NARROW_SCREEN]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gridTemplateAreas: '"buttons" "recipe" "numbers" "reason" "comment" "submit"',
    },
  },
  buttonsArea: {
    gridArea: 'buttons',
  },
  recipeArea: {
    gridArea: 'recipe',
  },
  numbersArea: {
    gridArea: 'numbers',
  },
  reasonArea: {
    gridArea: 'reason',
  },
  commentArea: {
    gridArea: 'comment',
  },
  submitArea: {
    gridArea: 'submit',
    [NARROW_SCREEN]: {
      width: '100%',
    },
  },
  fullWidth: {
    width: '100%'
  },
  formButtons: {
    display: 'flex',
    gap: '10px',
  },
  numbers: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(4, 100px)',
    [NARROW_SCREEN]: {
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
  },
  inputColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    fontWeight: 'bold',
    position: 'relative',
    minWidth: 0,
  },
  dropdown: {
    [NARROW_SCREEN]: {
      minWidth: 0,
    },
  },
  recipeTable: {
    maxWidth: '880px',
  },
  historyTable: {
    maxWidth: '880px',
  },
  alertsTable: {
    maxWidth: '1100px',
    maxHeight: '90vh',
  },
  alertsContent: {
    overflowY: 'auto',
  },
  noPaddingRight: {
    paddingRight: 0
  },
  computedValue: {
    fontSize: '11px',
    fontWeight: 'normal',
    color: '#616161',
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '2px',
  },
  errorText: {
    color: '#d13438',
    fontSize: '11px',
    marginTop: '2px',
    position: 'absolute',
    top: '100%',
    left: 0,
    whiteSpace: 'nowrap',
  },
});

const fetchRecipesData = async () => {
  const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/recipes`);
  return response.data;
};

const fetchRecipeHistory = async (recipeId) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/recipes/${recipeId}/history?limit=${RECIPE_HISTORY_LIMIT}`
  const response = await axios.get(url);
  return response.data;
}

const submitNewThresholds = async ({id, target, minBand, maxBand, reason, comment, username, numRcp}) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/recipes/${id}`;

  try {
    const response = await axios.put(url, {
      eTarget: target,
      eMin: target - minBand,
      eMax: target + maxBand,
      eMinBand: minBand,
      eMaxBand: maxBand,
      numRcp: numRcp,
      eSource: "rd_frontend",
      eUsername: username,
      eComment: comment,
      eReason: reason || null,
    }, {
      headers: { 'Content-Type': 'application/json' }
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(`HTTP error! status: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('No response received from server.');
    } else {
      throw new Error(`Error in setting up request: ${error.message}`);
    }
  }
}

const extractRecipeId = (recipeFull) => {
  if (recipeFull) {
    const match = recipeFull.match(/\((\d+)\)$/);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }
  }
  return null
};

const makeRecipeFull = ({id, name}) => `${name} (${id})`;

const UpdateTargetForm = () => {
  const {recipeFull, setRecipeFull} = useData();
  const [recipes, setRecipes] = useState({});
  const [target, setTarget] = useState('');
  const [numRcp, setNumRcp] = useState('');
  const [minBand, setMinBand] = useState('');
  const [maxBand, setMaxBand] = useState('');
  const [minBandError, setMinBandError] = useState(null);
  const [maxBandError, setMaxBandError] = useState(null);
  const [targetError, setTargetError] = useState(null);
  const [numRcpError, setNumRcpError] = useState(null);
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [isStateDirty, setIsStateDirty] = useState(true);
  const [shouldUpdateCurrentRecipe, setShouldUpdateCurrentRecipe] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isHistoryItemsLoading, setIsHistoryItemsLoading] = useState(false);
  const [historyItemsId, setHistoryItemsId] = useState("");
  const computedMin = (target !== '' && minBand !== '') ? (parseFloat(target) - parseFloat(minBand)) : null;
  const computedMax = (target !== '' && maxBand !== '') ? (parseFloat(target) + parseFloat(maxBand)) : null;

  const [historyItems, setHistoryItems] = useState([]);
  const [isAlertsDialogOpen, setIsAlertsDialogOpen] = useState(false);
  const {accounts} = useMsal();
  const styles = useStyles();

  const username = accounts.length > 0 ? accounts[0].username : '';

  const isCurrentData = () => {
    if (!!recipeFull) {
      const recipeId = extractRecipeId(recipeFull);
      const recipe = recipes[recipeId];
      return (
        recipe?.currentTarget === target
          && recipe?.currentMinBand === minBand
          && recipe?.currentMaxBand === maxBand
          && (recipe?.currentNumRcp ?? '') === numRcp
      );
    }
    return false;
  };

  const updateCurrentRecipe = useCallback((_recipeFull) => {
    const recipeId = extractRecipeId(_recipeFull);
    const recipe = recipes[recipeId];
    if (recipe) {
      setTarget(recipe.currentTarget ?? '');
      setNumRcp(recipe.currentNumRcp ?? '');
      setMinBand(recipe.currentMinBand ?? '');
      setMaxBand(recipe.currentMaxBand ?? '');
      setComment("");
      setReason("");
      setMinBandError(null);
      setMaxBandError(null);
      setTargetError(null);
      setNumRcpError(null);
    }
  }, [recipes]);

  useEffect(() => {
    if (isStateDirty) {
      fetchRecipesData().then((data) => {
        const _recipes = Object.fromEntries(data.map(d => ([d.iCodeRecette, {
          id: d.iCodeRecette,
          name: d.sDescription,
          currentTarget: d.eTarget,
          currentNumRcp: d.numRcp,
          currentThresholdMin: d.eMin,
          currentThresholdMax: d.eMax,
          currentMinBand: d.eMinBand,
          currentMaxBand: d.eMaxBand,
          currentReason: d.eReason,
          currentComment: d.eComment,
          updatedAt: d.eUpdatedAt
        }])));
        setRecipes(_recipes);
        setIsStateDirty(false);
        setLastRefreshed(new Date());
      });
    }
  }, [isStateDirty]);

  useEffect(() => {
    if (!!recipeFull && shouldUpdateCurrentRecipe) {
      updateCurrentRecipe(recipeFull);
    }
  }, [recipeFull, shouldUpdateCurrentRecipe, updateCurrentRecipe]);

  const handleRecipeChange = (_, data) => {
    setRecipeFull(data.optionValue);
    updateCurrentRecipe(data.optionValue);
  };

  const validateNumRcp = (val) => {
    if (val === '') return null;
    const num = parseFloat(val);
    if (!Number.isInteger(num) || num < 0) return "Entier positif ou 0";
    return null;
  };

  const handleNumRcpChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setNumRcp('');
      setNumRcpError(null);
      return;
    }
    const newValue = parseFloat(value);
    if (!isNaN(newValue)) {
      setNumRcp(newValue);
      setNumRcpError(validateNumRcp(newValue));
    }
  };

  const validateTarget = (val) => {
    if (val === '') return null;
    const num = parseFloat(val);
    if ((num * 10) % 5 !== 0) return "Incréments de 0.5 seulement";
    return null;
  };

  const validateBand = (val) => {
    if (val === '') return null;
    const num = parseFloat(val);
    if (!Number.isInteger(num)) return "Doit être un entier";
    return null;
  };

  const handleTargetChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setTarget('');
      setTargetError(null);
      return;
    }
    const newValue = parseFloat(value);
    if (!isNaN(newValue)) {
      setTarget(newValue);
      setTargetError(validateTarget(newValue));
    }
  };

  const handleBandMinChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setMinBand('');
      setMinBandError(null);
      return;
    }
    const newValue = parseFloat(value);
    if (!isNaN(newValue)) {
      if (newValue > 20) {
        setMinBand(20);
        setMinBandError(validateBand(20));
      } else if (newValue < 1) {
        setMinBand(1);
        setMinBandError(validateBand(1));
      } else {
        setMinBand(newValue);
        setMinBandError(validateBand(newValue));
      }
    }
  };

  const handleBandMaxChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setMaxBand('');
      setMaxBandError(null);
      return;
    }
    const newValue = parseFloat(value);
    if (!isNaN(newValue)) {
      if (newValue > 20) {
        setMaxBand(20);
        setMaxBandError(validateBand(20));
      } else if (newValue < 1) {
        setMaxBand(1);
        setMaxBandError(validateBand(1));
      } else {
        setMaxBand(newValue);
        setMaxBandError(validateBand(newValue));
      }
    }
  };

  const handleCommentChange = (event) => {
    const newValue = event.target.value;
    setComment(newValue);
  };

  const handleReasonChange = (_, data) => {
    setReason(data.optionValue ?? '');
  };

  const handleDismissMessage = () => {
    setErrorMessage("");
    setShowMessage(false);
  };

  const handleConfirmation = async () => {
    const recipeId = extractRecipeId(recipeFull);
    try {
      const response = await submitNewThresholds({
        id: recipeId,
        username,
        target,
        minBand,
        maxBand,
        reason,
        comment,
        numRcp
      });
      console.log('Successfully changed threshold', response);
    } catch (error) {
      setErrorMessage(`Une erreur est survenue lors des changements apportés à la recette "${recipeFull}"`);
      console.error(`Error while changing recipe "${recipeFull}"`, error);
    } finally {
      setShouldUpdateCurrentRecipe(true);
      setIsStateDirty(true);
      setShowMessage(true);
      setHistoryItemsId("");
      setTimeout(handleDismissMessage, 3000);
    }
  };

  const handleHistoryButtonClick = async () => {
    const recipeId = extractRecipeId(recipeFull);

    if (recipeId !== historyItemsId && !isHistoryItemsLoading) {
      setIsHistoryItemsLoading(true);
      setHistoryItemsId(parseInt(recipeId));

      const history = await fetchRecipeHistory(recipeId);

      setHistoryItems(history);
      setIsHistoryItemsLoading(false);
    }
  }

  const refreshTooltipContent = () => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - lastRefreshed.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return `Il y a moins d'une minute`;
    }

    if (diffInMinutes < 60) {
      return `Il y a ${diffInMinutes} minutes`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    return `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
  }

  const renderMessage = () => {
    const messageContent = errorMessage || "Les changements ont bien été effectués!";
    return (
      <MessageBar
        intent={errorMessage ? "error" : "success"}
      >
        <MessageBarBody>
          <MessageBarTitle>{errorMessage ? "Erreur" : "Succès"}</MessageBarTitle>
          {messageContent}
        </MessageBarBody>
        <MessageBarActions
          containerAction={
            <Button
              aria-label="dismiss"
              appearance="transparent"
              icon={<DismissRegular/>}
              onClick={handleDismissMessage}
            />
          }
        />
      </MessageBar>
    );
  };

  const renderTableDialog = () => {
    const selectedId = recipeFull && extractRecipeId(recipeFull);
    const onSelect = (recipeId) => {
      setRecipeFull(makeRecipeFull(recipes[recipeId]));
    };

    return (
      <Dialog>
        <DialogTrigger disableButtonEnhancement>
          <Tooltip content="Voir toutes les recettes">
            <Button icon={<TableRegular/>}/>
          </Tooltip>
        </DialogTrigger>
        <DialogSurface className={styles.recipeTable}>
          <DialogBody>
            <DialogTitle>Toutes les recettes</DialogTitle>
            <DialogContent className={styles.noPaddingRight}>
              <RecipesTargetTable
                recipes={Object.values(recipes)}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Fermer</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  };

  const renderHistoryDialog = () => {
    return (
      <Dialog>
        <DialogTrigger>
          <Tooltip content="Historique des changements">
            <Button
              icon={<HistoryRegular/>}
              onClick={handleHistoryButtonClick}
              disabled={!recipeFull}
            />
          </Tooltip>
        </DialogTrigger>
        <DialogSurface className={styles.historyTable}>
          <DialogBody>
            <DialogTitle>Historique des changements</DialogTitle>
            <DialogContent className={styles.noPaddingRight}>
              <RecipeHistoryTable
                recipeFull={recipeFull}
                historyItems={historyItems}
                isLoading={isHistoryItemsLoading}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Fermer</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  };

  const renderAlertsDialog = () => {
    return (
      <Dialog
        open={isAlertsDialogOpen}
        onOpenChange={(_, data) => setIsAlertsDialogOpen(data.open)}
      >
        <DialogTrigger disableButtonEnhancement>
          <Tooltip content="Historique des alertes">
            <Button icon={<AlertRegular />} />
          </Tooltip>
        </DialogTrigger>
        <DialogSurface className={styles.alertsTable}>
          <DialogBody>
            <DialogTitle>Historique des alertes</DialogTitle>
            <DialogContent className={`${styles.noPaddingRight} ${styles.alertsContent}`}>
              <AlertsTable isOpen={isAlertsDialogOpen} />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Fermer</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  };

  const renderDropdown = () => (
    <div className={mergeClasses(styles.inputColumn, styles.recipeArea)}>
      <label htmlFor="input-recipe-dropdown">Recette</label>
      <Dropdown
        id="input-recipe-dropdown"
        className={styles.dropdown}
        positioning={DROPDOWN_POSITIONING}
        placeholder="Sélectionner une recette"
        onOptionSelect={handleRecipeChange}
        value={recipeFull}
      >
        {Object.values(recipes).map(({id, name}) => (
          <Option key={id}>{makeRecipeFull({id, name})}</Option>
        ))}
      </Dropdown>
    </div>
  );

  const renderReasonDropdown = () => {
    const selectedReason = REASON_OPTIONS.find(({value}) => value === reason);

    return (
      <div className={mergeClasses(styles.inputColumn, styles.reasonArea)}>
        <label htmlFor="input-reason-dropdown">Raison</label>
        <Dropdown
          id="input-reason-dropdown"
          className={styles.dropdown}
          positioning={DROPDOWN_POSITIONING}
          placeholder="Sélectionner une raison"
          selectedOptions={selectedReason ? [selectedReason.value] : []}
          value={selectedReason?.label ?? ''}
          onOptionSelect={handleReasonChange}
        >
          {REASON_OPTIONS.map(({value, label}) => (
            <Option key={value} value={value}>{label}</Option>
          ))}
        </Dropdown>
      </div>
    );
  };

  const renderSubmitDialog = () => {
    const recipeId = extractRecipeId(recipeFull);
    const hasErrors = !!(minBandError || maxBandError || targetError || numRcpError);
    const disabled = !recipeFull || target === '' || minBand === '' || maxBand === '' || numRcp === '' || isCurrentData() || hasErrors;
    const recipe = recipeId ? recipes[recipeId] : {};

    return (
      <Dialog>
        <DialogTrigger>
          <Button
            className={styles.submitArea}
            appearance="primary"
            disabled={disabled}
            onClick={() => {
              setShouldUpdateCurrentRecipe(false);
              setIsStateDirty(true);
            }}
          >
            Soumettre
          </Button>
        </DialogTrigger>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              Confirmer la Soumission
            </DialogTitle>
            <DialogContent>
              Modifications pour <strong>{recipeFull}</strong>: <br />
              Le <strong>Num Rcp</strong> passera de {recipe?.currentNumRcp ?? 'N/A'} à <strong>{numRcp}</strong>. <br />
              Le <strong>bande minimal</strong> passera de {recipe?.currentMinBand || 'N/A'} mm à <strong>{minBand} mm</strong> (Seuil Min : <strong>{computedMin !== null ? parseFloat(computedMin.toFixed(2)) : 'N/A'} mm</strong>). <br />
              La <strong>cible</strong> passera de {recipe?.currentTarget || 'N/A'} mm à <strong>{target}</strong> mm. <br />
              Le <strong>bande maximal</strong> passera de {recipe?.currentMaxBand || 'N/A'} mm à <strong>{maxBand}</strong> mm (Seuil Max : <strong>{computedMax !== null ? parseFloat(computedMax.toFixed(2)) : 'N/A'} mm</strong>). <br />
              Raison : <strong>{getReasonLabel(reason) || 'Aucune'}</strong>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Annuler</Button>
              </DialogTrigger>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="primary" onClick={handleConfirmation}>Confirmer</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  };

  return (
    <div className={styles.root}>
      <form>
        <div className={styles.formRoot}>
          <div className={mergeClasses(styles.formButtons, styles.buttonsArea)}>
            <div className={styles.configButton}>
              <GrafanaDashboardConfig />
            </div>
            <Tooltip
              content={refreshTooltipContent()}
              appearance="inverted"
              relationship="description"
            >
              <Button
                icon={<ArrowClockwiseFilled />}
                onClick={() => setIsStateDirty(true)} appearance="primary"
              />
            </Tooltip>
            {renderTableDialog()}
            {renderHistoryDialog()}
            {renderAlertsDialog()}
          </div>
          {renderDropdown()}
          <div className={mergeClasses(styles.numbers, styles.numbersArea)}>
            <div className={styles.inputColumn}>
              <label
                htmlFor="input-num-rcp">Num Rcp</label>
              <Input
                id="input-num-rcp"
                type="number"
                min={0}
                step="1"
                value={numRcp}
                onChange={handleNumRcpChange}
              />
              {numRcpError && (
                <span className={styles.errorText}>{numRcpError}</span>
              )}
            </div>
            <div className={styles.inputColumn}>
              <label
                htmlFor="input-band-min">Bande Min</label>
              <Input
                id="input-band-min"
                type="number"
                step="1"
                min={1}
                max={20}
                value={minBand}
                onChange={handleBandMinChange}
              />
              {minBandError ? (
                <span className={styles.errorText}>{minBandError}</span>
              ) : computedMin !== null ? (
                <span className={styles.computedValue}>
                  Seuil Min: {parseFloat(computedMin.toFixed(2))}
                </span>
              ) : null}
            </div>
            <div className={styles.inputColumn}>
              <label
                htmlFor="input-target">Cible</label>
              <Input
                id="input-target"
                type="number"
                step="0.5"
                value={target}
                onChange={handleTargetChange}
              />
              {targetError && (
                <span className={styles.errorText}>{targetError}</span>
              )}
            </div>
            <div className={styles.inputColumn}>
              <label
                htmlFor="input-band-max">Bande Max</label>
              <Input
                id="input-band-max"
                min={1}
                max={20}
                type="number"
                step="1"
                value={maxBand}
                onChange={handleBandMaxChange}
              />
              {maxBandError ? (
                <span className={styles.errorText}>{maxBandError}</span>
              ) : computedMax !== null ? (
                <span className={styles.computedValue}>
                  Seuil Max: {parseFloat(computedMax.toFixed(2))}
                </span>
              ) : null}
            </div>
          </div>
          {renderReasonDropdown()}
          <div className={mergeClasses(styles.inputColumn, styles.commentArea)}>
            <label htmlFor="input-comment">Commentaire</label>
            <Input
              id="input-comment"
              placeholder="Entrer un commentaire"
              value={comment}
              onChange={handleCommentChange}
            />
          </div>
          {renderSubmitDialog()}
        </div>
        {showMessage && renderMessage()}
      </form>
    </div>
  );
};

export default UpdateTargetForm;
